import { uniq } from 'lodash/fp';

import { WorkflowActionMeta } from 'dashboard/models/workflow';
import { GenericWorkflowAction, WorkflowSchema } from 'dashboard/models/workflow-schema';
import {
  AllWorkflowAction,
  DeprecatedWorkflowStepActionKey,
  HttpPlatformIntegrationConfig,
  WorkflowStepActionKey,
  WorkflowStepInput,
} from 'dashboard/models/workflow-version';
import { EVENT_NAME_TO_EVENT_TYPE, EventName, EventType } from 'lib/constants/events';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';
import { eventTypeForComputedFieldTrigger } from 'lib/workflows';

export const ACTION_TO_REQUIRED_REPORT_TEMPLATE_ID: Partial<
  Record<WorkflowStepActionKey, ApiModel>
> = {
  [WorkflowStepActionKey.RunReportAddressLookup]: ApiModel.ReportTemplateAddressLookup,
  [WorkflowStepActionKey.RunReportAdverseMedia]: ApiModel.ReportTemplateAdverseMedia,
  [WorkflowStepActionKey.RunReportBusinessAdverseMedia]:
    ApiModel.ReportTemplateBusinessAdverseMedia,
  [WorkflowStepActionKey.RunReportBusinessAssociatedPersons]:
    ApiModel.ReportTemplateBusinessAssociatedPersons,
  [WorkflowStepActionKey.RunReportBusinessLookup]: ApiModel.ReportTemplateBusinessLookup,
  [WorkflowStepActionKey.RunReportBusinessSocialMedia]: ApiModel.ReportTemplateBusinessSocialMedia,
  [WorkflowStepActionKey.RunReportBusinessWatchlist]: ApiModel.ReportTemplateBusinessWatchlist,
  [WorkflowStepActionKey.RunReportCryptoAddressWatchlist]:
    ApiModel.ReportTemplateCryptoAddressWatchlist,
  [WorkflowStepActionKey.RunReportCustomList]: ApiModel.ReportTemplateCustomList,
  [WorkflowStepActionKey.RunReportEmailAddress]: ApiModel.ReportTemplateEmailAddress,
  [WorkflowStepActionKey.RunReportPhoneNumber]: ApiModel.ReportTemplatePhoneNumber,
  [WorkflowStepActionKey.RunReportPoliticallyExposedPerson]:
    ApiModel.ReportTemplatePoliticallyExposedPerson,
  [WorkflowStepActionKey.RunReportProfile]: ApiModel.ReportTemplateProfile,
  [WorkflowStepActionKey.RunReportProfileNonAuthoritative]:
    ApiModel.ReportTemplateProfileNonAuthoritative,
  [WorkflowStepActionKey.RunReportScreening]: ApiModel.ReportTemplateScreening,
  [WorkflowStepActionKey.RunReportSocialMedia]: ApiModel.ReportTemplateSocialMedia,
  [WorkflowStepActionKey.RunReportSynthetic]: ApiModel.ReportTemplateSynthetic,
  [WorkflowStepActionKey.RunReportWatchlist]: ApiModel.ReportTemplateWatchlist,
};

export enum ActionGroup {
  Analytics = 'analytics',
  Accounts = 'accounts',
  Case = 'case',
  Communicate = 'communicate',
  Compute = 'compute',
  Graph = 'graph',
  Inquiry = 'inquiry',
  InquiryFlow = 'inquiry_flow',
  List = 'list',
  Marketplace = 'marketplace',
  Other = 'other',
  Report = 'report',
  Transaction = 'transaction',
  Verification = 'verification',
}

export enum SubActionGroup {
  Chainalysis = 'integration_chainalysis',
  Clearbit = 'integration_clearbit',
  Coinbase = 'integration_coinbase',
  Equifax = 'integration_equifax',
  Hubspot = 'integration_hubspot',
  Middesk = 'integration_middesk',
  MxBank = 'integration_mx',
  NovaCredit = 'integration_nova_credit',
  Kyckr = 'integration_kyckr',
  OpenAi = 'integration_openai',
  Salesforce = 'integration_salesforce',
  Sentilink = 'integration_sentilink',
  Slack = 'integration_slack',
  Trm = 'integration_trm',
  Zendesk = 'integration_zendesk',
}

export type SubAction = {
  value: SubActionGroup;
  label: string;
};

export type ActionMetaData = {
  key: AllWorkflowAction['key'];
  group: ActionGroup;
  label: string;
  enhanced?: boolean;
  subGroup?: SubAction;
  hidden?: boolean;
  integration?: HttpPlatformIntegrationConfig['integration'];
};

export type ActionMetaDataMap = Record<
  WorkflowStepActionKey | DeprecatedWorkflowStepActionKey | GenericWorkflowAction['key'],
  ActionMetaData
>;

export const getActionMetaDataFromGenericConfigs = (
  workflowActions: WorkflowSchema['generic-workflow-actions'],
): ActionMetaData[] =>
  workflowActions.map((action) => ({
    key: action.key,
    group: action.category,
    label: action.title,
    subGroup: action.subCategory,
    integration: (action as HttpPlatformIntegrationConfig).integration,
  }));

export const getActionKeyToMetaDataMap = (meta?: WorkflowActionMeta): ActionMetaDataMap => {
  const runReportLabelFor = (key: WorkflowStepActionKey) => {
    const name = meta?.['report-templates'].find(
      ({ type }) => type === ACTION_TO_REQUIRED_REPORT_TEMPLATE_ID[key],
    )?.name;
    return name ? `Run ${name}` : null;
  };

  return {
    // TODO(sal): Either get rid of the `key` from this Record<> or the `key` from the ActionMetaData.
    [WorkflowStepActionKey.AddCaseToCaseQueue]: {
      key: WorkflowStepActionKey.AddCaseToCaseQueue,
      group: ActionGroup.Case,
      label: 'Add Case to Queue',
    },
    [WorkflowStepActionKey.AddCommentToCase]: {
      key: WorkflowStepActionKey.AddCommentToCase,
      group: ActionGroup.Case,
      label: 'Add Comment to Case',
    },
    [WorkflowStepActionKey.AddItemToList]: {
      key: WorkflowStepActionKey.AddItemToList,
      group: ActionGroup.List,
      label: 'Add Item to List',
    },
    [WorkflowStepActionKey.AddItemToListFace]: {
      key: WorkflowStepActionKey.AddItemToListFace,
      group: ActionGroup.List,
      label: 'Add Item to Face List',
    },
    [WorkflowStepActionKey.ApproveInquiry]: {
      key: WorkflowStepActionKey.ApproveInquiry,
      group: ActionGroup.Inquiry,
      label: 'Approve Inquiry',
    },
    [WorkflowStepActionKey.AssignCase]: {
      key: WorkflowStepActionKey.AssignCase,
      group: ActionGroup.Case,
      label: 'Assign Case',
    },
    [WorkflowStepActionKey.AttachObjects]: {
      key: WorkflowStepActionKey.AttachObjects,
      group: ActionGroup.Other,
      label: 'Attach Objects',
    },
    [WorkflowStepActionKey.AttachObjectsToCase]: {
      key: WorkflowStepActionKey.AttachObjectsToCase,
      group: ActionGroup.Case,
      label: 'Attach Objects to Case',
    },
    [WorkflowStepActionKey.AttachObjectsToSAR]: {
      key: WorkflowStepActionKey.AttachObjectsToSAR,
      group: ActionGroup.Case,
      label: 'Attach Objects to SAR',
    },
    [WorkflowStepActionKey.AutofillAttachedObjectsToSAR]: {
      key: WorkflowStepActionKey.AutofillAttachedObjectsToSAR,
      group: ActionGroup.Case,
      label: 'Autofill SAR with objects',
    },
    [WorkflowStepActionKey.Calculate]: {
      key: WorkflowStepActionKey.Calculate,
      group: ActionGroup.Compute,
      label: 'Calculate',
    },
    [WorkflowStepActionKey.CopyTags]: {
      key: WorkflowStepActionKey.CopyTags,
      group: ActionGroup.Other,
      label: 'Copy Tags',
    },
    [WorkflowStepActionKey.FindOrCreateAccount]: {
      key: WorkflowStepActionKey.FindOrCreateAccount,
      group: ActionGroup.Accounts,
      label: 'Find or Create Account',
    },
    [WorkflowStepActionKey.FindOrCreateTransaction]: {
      key: WorkflowStepActionKey.FindOrCreateTransaction,
      group: ActionGroup.Transaction,
      label: 'Find or Create Transaction',
    },
    [WorkflowStepActionKey.CreateCase]: {
      key: WorkflowStepActionKey.CreateCase,
      group: ActionGroup.Case,
      label: 'Create Case',
    },
    [WorkflowStepActionKey.CreateSAR]: {
      key: WorkflowStepActionKey.CreateSAR,
      group: ActionGroup.Case,
      label: 'Create SAR',
    },
    [WorkflowStepActionKey.CreateDocumentGovernmentId]: {
      key: WorkflowStepActionKey.CreateDocumentGovernmentId,
      group: ActionGroup.Verification,
      label: 'Create Gov Id Document',
    },
    [WorkflowStepActionKey.ChainalysisAddressScreening]: {
      key: WorkflowStepActionKey.ChainalysisAddressScreening,
      group: ActionGroup.Marketplace,
      label: 'Run Chainalysis Address Screening Report',
      subGroup: { value: SubActionGroup.Chainalysis, label: 'Chainalysis' },
    },
    [WorkflowStepActionKey.ClearbitBusinessLookup]: {
      key: WorkflowStepActionKey.ClearbitBusinessLookup,
      group: ActionGroup.Marketplace,
      label: 'Get Clearbit Report',
      subGroup: { value: SubActionGroup.Clearbit, label: 'Clearbit' },
    },
    [WorkflowStepActionKey.CoinbaseCheckCryptoRisk]: {
      key: WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
      group: ActionGroup.Marketplace,
      label: 'Check Coinbase Crypto Risk',
      subGroup: { value: SubActionGroup.Coinbase, label: 'Coinbase' },
    },
    [WorkflowStepActionKey.CreateInquiry]: {
      key: WorkflowStepActionKey.CreateInquiry,
      group: ActionGroup.Inquiry,
      label: 'Create Inquiry',
    },
    [WorkflowStepActionKey.DeclineInquiry]: {
      key: WorkflowStepActionKey.DeclineInquiry,
      group: ActionGroup.Inquiry,
      label: 'Decline Inquiry',
    },
    [WorkflowStepActionKey.EmailInquiryLink]: {
      key: WorkflowStepActionKey.EmailInquiryLink,
      group: ActionGroup.Inquiry,
      label: 'Inquiry Link Email',
    },
    [WorkflowStepActionKey.EquifaxOneview]: {
      key: WorkflowStepActionKey.EquifaxOneview,
      group: ActionGroup.Marketplace,
      label: 'Run Equifax OneView Report',
      subGroup: { value: SubActionGroup.Equifax, label: 'Equifax' },
    },
    [WorkflowStepActionKey.EvaluateCode]: {
      key: WorkflowStepActionKey.EvaluateCode,
      group: ActionGroup.Compute,
      label: 'Evaluate Code',
      enhanced: true,
    },
    [WorkflowStepActionKey.FetchPersonaObject]: {
      key: WorkflowStepActionKey.FetchPersonaObject,
      group: ActionGroup.Compute,
      label: 'Fetch Persona Object',
    },
    [WorkflowStepActionKey.GPTGenerateMessage]: {
      key: WorkflowStepActionKey.GPTGenerateMessage,
      group: ActionGroup.Marketplace,
      label: 'GPT Generate Message',
      subGroup: { value: SubActionGroup.OpenAi, label: 'OpenAI' },
    },
    [WorkflowStepActionKey.ListMatch]: {
      key: WorkflowStepActionKey.ListMatch,
      group: ActionGroup.List,
      label: 'Get List Match',
    },
    [WorkflowStepActionKey.HttpRequest]: {
      key: WorkflowStepActionKey.HttpRequest,
      group: ActionGroup.Compute,
      label: 'Make HTTPS Request',
      enhanced: true,
    },
    [WorkflowStepActionKey.LabelObject]: {
      key: WorkflowStepActionKey.LabelObject,
      group: ActionGroup.Other,
      label: 'Label Object',
      enhanced: true,
    },
    [WorkflowStepActionKey.MarkInquiryForReview]: {
      key: WorkflowStepActionKey.MarkInquiryForReview,
      group: ActionGroup.Inquiry,
      label: 'Mark Inquiry for Review',
    },
    [WorkflowStepActionKey.MxAccount]: {
      key: WorkflowStepActionKey.MxAccount,
      group: ActionGroup.Marketplace,
      label: 'Run MX Bank Get Account Owners Report',
      enhanced: true,
      subGroup: { value: SubActionGroup.MxBank, label: 'MX Bank' },
    },
    [WorkflowStepActionKey.MxCreateUser]: {
      key: WorkflowStepActionKey.MxCreateUser,
      group: ActionGroup.Marketplace,
      label: 'MX Bank Create User',
      enhanced: true,
      subGroup: { value: SubActionGroup.MxBank, label: 'MX Bank' },
    },
    [WorkflowStepActionKey.MxGetMicrodepositStatus]: {
      key: WorkflowStepActionKey.MxGetMicrodepositStatus,
      group: ActionGroup.Marketplace,
      label: 'MX Bank Get Micro-Deposit Status',
      enhanced: true,
      subGroup: { value: SubActionGroup.MxBank, label: 'MX Bank' },
    },
    [WorkflowStepActionKey.MxRequestConnectWidget]: {
      key: WorkflowStepActionKey.MxRequestConnectWidget,
      group: ActionGroup.Marketplace,
      label: 'MX Bank Request Connect Widget',
      enhanced: true,
      subGroup: { value: SubActionGroup.MxBank, label: 'MX Bank' },
    },
    [WorkflowStepActionKey.NovaCreditCashAtlas]: {
      key: WorkflowStepActionKey.NovaCreditCashAtlas,
      group: ActionGroup.Marketplace,
      label: 'Fetch Cash Atlas Report',
      subGroup: { value: SubActionGroup.NovaCredit, label: 'NovaCredit' },
    },
    [WorkflowStepActionKey.NovaCreditCreditPassport]: {
      key: WorkflowStepActionKey.NovaCreditCreditPassport,
      group: ActionGroup.Marketplace,
      label: 'Fetch Credit Passport Report',
      subGroup: { value: SubActionGroup.NovaCredit, label: 'NovaCredit' },
    },
    [WorkflowStepActionKey.PauseReportsContinuousMonitoring]: {
      key: WorkflowStepActionKey.PauseReportsContinuousMonitoring,
      group: ActionGroup.Report,
      label: 'Pause Continuous Monitoring on Reports',
    },
    [WorkflowStepActionKey.Query]: {
      key: WorkflowStepActionKey.Query,
      group: ActionGroup.Compute,
      label: 'Query Persona objects',
    },
    [WorkflowStepActionKey.RemoveItemFromList]: {
      key: WorkflowStepActionKey.RemoveItemFromList,
      group: ActionGroup.List,
      label: 'Remove Item From List',
    },
    [WorkflowStepActionKey.RemoveTag]: {
      key: WorkflowStepActionKey.RemoveTag,
      group: ActionGroup.Other,
      label: 'Remove Tag',
    },
    [WorkflowStepActionKey.RedactObject]: {
      key: WorkflowStepActionKey.RedactObject,
      group: ActionGroup.Other,
      label: 'Redact Object',
    },
    [WorkflowStepActionKey.RunGraphQuery]: {
      key: WorkflowStepActionKey.RunGraphQuery,
      group: ActionGroup.Graph,
      label: 'Run Graph Query',
    },
    [WorkflowStepActionKey.RunKyckrBusinessLookup]: {
      key: WorkflowStepActionKey.RunKyckrBusinessLookup,
      group: ActionGroup.Marketplace,
      label: 'Run Kyckr Business Lookup',
      subGroup: { value: SubActionGroup.Kyckr, label: 'Kyckr' },
    },
    [WorkflowStepActionKey.RunReportAddressLookup]: {
      key: WorkflowStepActionKey.RunReportAddressLookup,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportAddressLookup) ??
        'Run Address Lookup Report',
    },
    [WorkflowStepActionKey.RunReportAdverseMedia]: {
      key: WorkflowStepActionKey.RunReportAdverseMedia,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportAdverseMedia) ??
        'Run Adverse Media Report',
    },
    [WorkflowStepActionKey.RunReportBusinessAdverseMedia]: {
      key: WorkflowStepActionKey.RunReportBusinessAdverseMedia,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportBusinessAdverseMedia) ??
        'Run Business Adverse Media Report',
    },
    [WorkflowStepActionKey.RunReportBusinessAssociatedPersons]: {
      key: WorkflowStepActionKey.RunReportBusinessAssociatedPersons,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportBusinessAssociatedPersons) ??
        'Run Business Associated Persons Report',
    },
    [WorkflowStepActionKey.RunReportBusinessClassification]: {
      key: WorkflowStepActionKey.RunReportBusinessClassification,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportBusinessClassification) ??
        'Run Business Classification Report',
    },
    [WorkflowStepActionKey.RunReportBusinessIndustryClassification]: {
      key: WorkflowStepActionKey.RunReportBusinessIndustryClassification,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportBusinessIndustryClassification) ??
        'Run Business Industry Classification Report',
    },
    [WorkflowStepActionKey.RunReportBusinessLiensLookup]: {
      key: WorkflowStepActionKey.RunReportBusinessLiensLookup,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportBusinessLiensLookup) ??
        'Run Business Liens Report',
    },
    [WorkflowStepActionKey.RunReportBusinessLookup]: {
      key: WorkflowStepActionKey.RunReportBusinessLookup,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportBusinessLookup) ??
        'Run Business Lookup Report',
    },
    [WorkflowStepActionKey.RunReportBusinessNonprofit]: {
      key: WorkflowStepActionKey.RunReportBusinessNonprofit,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportBusinessNonprofit) ??
        'Run Business Nonprofit Report',
    },
    [WorkflowStepActionKey.RunReportBusinessOnlinePresence]: {
      key: WorkflowStepActionKey.RunReportBusinessOnlinePresence,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportBusinessOnlinePresence) ??
        'Run Business Online Presence Report',
    },
    [WorkflowStepActionKey.RunReportBusinessRegistrationsLookup]: {
      key: WorkflowStepActionKey.RunReportBusinessRegistrationsLookup,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportBusinessRegistrationsLookup) ??
        'Run Business Registrations Report',
    },
    [WorkflowStepActionKey.RunReportBusinessSocialMedia]: {
      key: WorkflowStepActionKey.RunReportBusinessSocialMedia,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportBusinessSocialMedia) ??
        'Run Business Social Media Report',
    },
    [WorkflowStepActionKey.RunReportBusinessWatchlist]: {
      key: WorkflowStepActionKey.RunReportBusinessWatchlist,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportBusinessWatchlist) ??
        'Run Business Watchlist Report',
    },
    [WorkflowStepActionKey.RunReportBusinessWebsite]: {
      key: WorkflowStepActionKey.RunReportBusinessWebsite,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportBusinessWebsite) ??
        'Run Business Website Report',
    },
    [WorkflowStepActionKey.RunReportCryptoAddressWatchlist]: {
      key: WorkflowStepActionKey.RunReportCryptoAddressWatchlist,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportCryptoAddressWatchlist) ??
        'Run Crypto Address Watchlist Report',
    },
    [WorkflowStepActionKey.RunReportCustomList]: {
      key: WorkflowStepActionKey.RunReportCustomList,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportCustomList) ?? 'Run Custom List Report',
    },
    [WorkflowStepActionKey.RunReportEmailAddress]: {
      key: WorkflowStepActionKey.RunReportEmailAddress,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportEmailAddress) ?? 'Run Email Risk Report',
    },
    [WorkflowStepActionKey.RunReportPhoneNumber]: {
      key: WorkflowStepActionKey.RunReportPhoneNumber,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportPhoneNumber) ?? 'Run Phone Risk Report',
    },
    [WorkflowStepActionKey.RunReportPoliticallyExposedPerson]: {
      key: WorkflowStepActionKey.RunReportPoliticallyExposedPerson,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportPoliticallyExposedPerson) ??
        'Run Politically Exposed Person Report',
    },
    [WorkflowStepActionKey.RunReportProfile]: {
      key: WorkflowStepActionKey.RunReportProfile,
      group: ActionGroup.Report,
      label: runReportLabelFor(WorkflowStepActionKey.RunReportProfile) ?? 'Run Profile Report',
    },
    [WorkflowStepActionKey.RunReportProfileNonAuthoritative]: {
      key: WorkflowStepActionKey.RunReportProfileNonAuthoritative,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportProfileNonAuthoritative) ??
        'Run Profile Non-Authoritative Report',
    },
    [WorkflowStepActionKey.RunReportScreening]: {
      key: WorkflowStepActionKey.RunReportScreening,
      group: ActionGroup.Report,
      label: runReportLabelFor(WorkflowStepActionKey.RunReportScreening) ?? 'Run Screening Report',
    },
    [WorkflowStepActionKey.RunReportSocialMedia]: {
      key: WorkflowStepActionKey.RunReportSocialMedia,
      group: ActionGroup.Report,
      label:
        runReportLabelFor(WorkflowStepActionKey.RunReportSocialMedia) ?? 'Run Social Media Report',
    },
    [WorkflowStepActionKey.RunReportSynthetic]: {
      key: WorkflowStepActionKey.RunReportSynthetic,
      group: ActionGroup.Report,
      label: runReportLabelFor(WorkflowStepActionKey.RunReportSynthetic) ?? 'Run Synthetic Report',
    },
    [WorkflowStepActionKey.RunReportWatchlist]: {
      key: WorkflowStepActionKey.RunReportWatchlist,
      group: ActionGroup.Report,
      label: runReportLabelFor(WorkflowStepActionKey.RunReportWatchlist) ?? 'Run Watchlist Report',
    },
    [WorkflowStepActionKey.RunReportWithParams]: {
      key: WorkflowStepActionKey.RunReportWithParams,
      group: ActionGroup.Report,
      label: 'Run Report from Manual Input',
    },
    [WorkflowStepActionKey.RunReportGeneric]: {
      key: WorkflowStepActionKey.RunReportGeneric,
      group: ActionGroup.Report,
      label: 'Run Report',
    },
    [WorkflowStepActionKey.RunReportSentilinkApplicationRisk]: {
      key: WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
      group: ActionGroup.Marketplace,
      label: 'Run SentiLink Application Risk Report',
      subGroup: { value: SubActionGroup.Sentilink, label: 'SentiLink' },
    },
    [WorkflowStepActionKey.RunReportSentilinkScores]: {
      key: WorkflowStepActionKey.RunReportSentilinkScores,
      group: ActionGroup.Marketplace,
      label: 'Run SentiLink Scores Report',
      subGroup: { value: SubActionGroup.Sentilink, label: 'SentiLink' },
    },
    [WorkflowStepActionKey.RunVerificationGeneric]: {
      key: WorkflowStepActionKey.RunVerificationGeneric,
      group: ActionGroup.Verification,
      label: 'Run Verification',
    },
    [WorkflowStepActionKey.SendConfirmationCode]: {
      key: WorkflowStepActionKey.SendConfirmationCode,
      group: ActionGroup.Verification,
      label: 'Send Confirmation Code',
    },
    [WorkflowStepActionKey.AttemptConfirmationCode]: {
      key: WorkflowStepActionKey.AttemptConfirmationCode,
      group: ActionGroup.Verification,
      label: 'Attempt Confirmation Code',
    },
    [WorkflowStepActionKey.ScheduleWorkflowRun]: {
      key: WorkflowStepActionKey.ScheduleWorkflowRun,
      group: ActionGroup.Other,
      label: 'Schedule Workflow',
    },
    [WorkflowStepActionKey.SendCaseMessage]: {
      key: WorkflowStepActionKey.SendCaseMessage,
      group: ActionGroup.Case,
      label: 'Send Case Message',
    },
    [WorkflowStepActionKey.SendEmail]: {
      key: WorkflowStepActionKey.SendEmail,
      group: ActionGroup.Communicate,
      label: 'Send Email',
      enhanced: true,
    },
    [WorkflowStepActionKey.SendSlackMessage]: {
      key: WorkflowStepActionKey.SendSlackMessage,
      group: ActionGroup.Marketplace,
      label: '[Deprecated] Send Slack Message',
      enhanced: true,
      subGroup: { value: SubActionGroup.Slack, label: 'Slack' },
    },
    [WorkflowStepActionKey.SlackOAuthSendMessage]: {
      key: WorkflowStepActionKey.SlackOAuthSendMessage,
      group: ActionGroup.Marketplace,
      label: 'Send Slack Message OAuth',
      enhanced: true,
      subGroup: { value: SubActionGroup.Slack, label: 'Slack' },
    },
    [WorkflowStepActionKey.SendSms]: {
      key: WorkflowStepActionKey.SendSms,
      group: ActionGroup.Communicate,
      label: 'Send SMS',
      enhanced: true,
    },
    [WorkflowStepActionKey.SubmitMiddeskBusinessLookup]: {
      key: WorkflowStepActionKey.SubmitMiddeskBusinessLookup,
      group: ActionGroup.Marketplace,
      label: 'Run Middesk Business Report',
      enhanced: true,
      subGroup: { value: SubActionGroup.Middesk, label: 'Middesk' },
    },
    [WorkflowStepActionKey.SmsInquiryLink]: {
      key: WorkflowStepActionKey.SmsInquiryLink,
      group: ActionGroup.Inquiry,
      label: 'Send Inquiry Link SMS',
    },
    [WorkflowStepActionKey.SyncGraph]: {
      key: WorkflowStepActionKey.SyncGraph,
      group: ActionGroup.Graph,
      label: 'Sync Graph',
    },
    [WorkflowStepActionKey.ZendeskAddTagsToTicket]: {
      key: WorkflowStepActionKey.ZendeskAddTagsToTicket,
      group: ActionGroup.Marketplace,
      label: 'Add Tags to Zendesk Ticket',
      enhanced: true,
      subGroup: { value: SubActionGroup.Zendesk, label: 'Zendesk' },
    },
    [WorkflowStepActionKey.ZendeskOAuthCreateTicket]: {
      key: WorkflowStepActionKey.ZendeskOAuthCreateTicket,
      group: ActionGroup.Marketplace,
      label: 'Create Zendesk Ticket OAuth',
      enhanced: true,
      subGroup: { value: SubActionGroup.Zendesk, label: 'Zendesk' },
    },
    [WorkflowStepActionKey.ZendeskOAuthUpdateTicket]: {
      key: WorkflowStepActionKey.ZendeskOAuthUpdateTicket,
      group: ActionGroup.Marketplace,
      label: 'Update Zendesk Ticket OAuth',
      enhanced: true,
      subGroup: { value: SubActionGroup.Zendesk, label: 'Zendesk' },
    },
    [WorkflowStepActionKey.ZendeskCreateTicket]: {
      key: WorkflowStepActionKey.ZendeskCreateTicket,
      group: ActionGroup.Marketplace,
      label: '[Deprecated] Create Zendesk Ticket',
      enhanced: true,
      subGroup: { value: SubActionGroup.Zendesk, label: 'Zendesk' },
    },
    [WorkflowStepActionKey.ZendeskRemoveTagsFromTicket]: {
      key: WorkflowStepActionKey.ZendeskRemoveTagsFromTicket,
      group: ActionGroup.Marketplace,
      label: 'Remove Tags from Zendesk Ticket',
      enhanced: true,
      subGroup: { value: SubActionGroup.Zendesk, label: 'Zendesk' },
    },
    [WorkflowStepActionKey.ZendeskUpdateTicket]: {
      key: WorkflowStepActionKey.ZendeskUpdateTicket,
      group: ActionGroup.Marketplace,
      label: '[Deprecated] Update Zendesk Ticket',
      enhanced: true,
      subGroup: { value: SubActionGroup.Zendesk, label: 'Zendesk' },
    },
    [WorkflowStepActionKey.HubspotOAuthCreateObject]: {
      key: WorkflowStepActionKey.HubspotOAuthCreateObject,
      group: ActionGroup.Marketplace,
      label: 'Create Hubspot Object OAuth',
      enhanced: true,
      subGroup: { value: SubActionGroup.Hubspot, label: 'Hubspot' },
    },
    [WorkflowStepActionKey.HubspotOAuthSearchObject]: {
      key: WorkflowStepActionKey.HubspotOAuthSearchObject,
      group: ActionGroup.Marketplace,
      label: 'Search Hubspot Object OAuth',
      enhanced: true,
      subGroup: { value: SubActionGroup.Hubspot, label: 'Hubspot' },
    },
    [WorkflowStepActionKey.HubspotOAuthUpdateObject]: {
      key: WorkflowStepActionKey.HubspotOAuthUpdateObject,
      group: ActionGroup.Marketplace,
      label: 'Update Hubspot Object OAuth',
      enhanced: true,
      subGroup: { value: SubActionGroup.Hubspot, label: 'Hubspot' },
    },
    [WorkflowStepActionKey.HubspotUpdateObject]: {
      key: WorkflowStepActionKey.HubspotUpdateObject,
      group: ActionGroup.Marketplace,
      label: '[Deprecated] Update Hubspot Object',
      enhanced: true,
      subGroup: { value: SubActionGroup.Hubspot, label: 'Hubspot' },
    },
    [WorkflowStepActionKey.SalesforceCreateObject]: {
      key: WorkflowStepActionKey.SalesforceCreateObject,
      group: ActionGroup.Marketplace,
      label: '[Deprecated] Create Salesforce Record',
      enhanced: true,
      subGroup: { value: SubActionGroup.Salesforce, label: 'Salesforce' },
    },
    [WorkflowStepActionKey.SalesforceOAuthCreateRecord]: {
      key: WorkflowStepActionKey.SalesforceOAuthCreateRecord,
      group: ActionGroup.Marketplace,
      label: 'Create Salesforce Record OAuth',
      enhanced: true,
      subGroup: { value: SubActionGroup.Salesforce, label: 'Salesforce' },
    },
    [WorkflowStepActionKey.SalesforceOAuthSearchRecord]: {
      key: WorkflowStepActionKey.SalesforceOAuthSearchRecord,
      group: ActionGroup.Marketplace,
      label: 'Search Salesforce Record OAuth',
      enhanced: true,
      subGroup: { value: SubActionGroup.Salesforce, label: 'Salesforce' },
    },
    [WorkflowStepActionKey.SalesforceOAuthUpdateRecord]: {
      key: WorkflowStepActionKey.SalesforceOAuthUpdateRecord,
      group: ActionGroup.Marketplace,
      label: 'Update Salesforce Record OAuth',
      enhanced: true,
      subGroup: { value: SubActionGroup.Salesforce, label: 'Salesforce' },
    },
    [WorkflowStepActionKey.SalesforceUpdateObject]: {
      key: WorkflowStepActionKey.SalesforceUpdateObject,
      group: ActionGroup.Marketplace,
      label: '[Deprecated] Update Salesforce Record',
      enhanced: true,
      subGroup: { value: SubActionGroup.Salesforce, label: 'Salesforce' },
    },
    [WorkflowStepActionKey.SetRelation]: {
      key: WorkflowStepActionKey.SetRelation,
      group: ActionGroup.Other,
      label: 'Set Relation',
    },
    [WorkflowStepActionKey.TagObject]: {
      key: WorkflowStepActionKey.TagObject,
      group: ActionGroup.Other,
      label: 'Tag Object',
    },
    [WorkflowStepActionKey.TrmWalletScreening]: {
      key: WorkflowStepActionKey.TrmWalletScreening,
      group: ActionGroup.Marketplace,
      label: 'Run TRM Wallet Screening Report',
      subGroup: { value: SubActionGroup.Trm, label: 'TRM' },
    },
    [WorkflowStepActionKey.TrustDeviceFromInquiry]: {
      key: WorkflowStepActionKey.TrustDeviceFromInquiry,
      group: ActionGroup.Inquiry,
      label: 'Trust Device from Inquiry',
    },
    [WorkflowStepActionKey.UnsetRelation]: {
      key: WorkflowStepActionKey.UnsetRelation,
      group: ActionGroup.Other,
      label: 'Unset Relation',
    },
    [WorkflowStepActionKey.UpdateAccountFromUserInput]: {
      key: WorkflowStepActionKey.UpdateAccountFromUserInput,
      group: ActionGroup.Other,
      label: 'Update Account',
    },
    [WorkflowStepActionKey.UpdateAccountStatus]: {
      key: WorkflowStepActionKey.UpdateAccountStatus,
      group: ActionGroup.Accounts,
      label: 'Update Account Status',
    },
    [WorkflowStepActionKey.SetCaseSla]: {
      key: WorkflowStepActionKey.SetCaseSla,
      group: ActionGroup.Case,
      label: 'Set Case SLA',
    },
    [WorkflowStepActionKey.ClearCaseSla]: {
      key: WorkflowStepActionKey.ClearCaseSla,
      group: ActionGroup.Case,
      label: 'Clear Case SLA',
    },
    [WorkflowStepActionKey.UpdateCaseStatus]: {
      key: WorkflowStepActionKey.UpdateCaseStatus,
      group: ActionGroup.Case,
      label: 'Update Case Status',
    },
    [WorkflowStepActionKey.UpdateTransactionStatus]: {
      key: WorkflowStepActionKey.UpdateTransactionStatus,
      group: ActionGroup.Transaction,
      label: 'Update Transaction Status',
    },
    [WorkflowStepActionKey.CreateTransaction]: {
      key: WorkflowStepActionKey.CreateTransaction,
      group: ActionGroup.Transaction,
      label: 'Create Transaction',
    },

    // Dynamic Flow workflows
    [WorkflowStepActionKey.CreateVerificationAamvaFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationAamvaFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run AAMVA Verification',
    },
    [WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Korean Digital Certificate Verification',
    },
    [WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Database Verification',
    },
    [WorkflowStepActionKey.CreateVerificationDatabaseAadhaarFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationDatabaseAadhaarFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Database Aadhaar Verification',
    },
    [WorkflowStepActionKey.CreateVerificationDatabaseStandardFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationDatabaseStandardFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Database Standard Verification',
    },
    [WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Database eCBSV Verification',
    },
    [WorkflowStepActionKey.CreateVerificationDigitalIdFinnishTrustNetworkFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationDigitalIdFinnishTrustNetworkFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Finnish Trust Network Verification',
    },
    [WorkflowStepActionKey.CreateVerificationDigitalIdOneIdFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationDigitalIdOneIdFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run OneID Verification',
    },
    [WorkflowStepActionKey.CreateVerificationDigitalIdSingpassFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationDigitalIdSingpassFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Singpass Verification',
    },
    [WorkflowStepActionKey.CreateVerificationDigitalIdUkSharecodeFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationDigitalIdUkSharecodeFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run UK Sharecode Verification',
    },
    [WorkflowStepActionKey.CreateVerificationDocumentFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationDocumentFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Document Verification',
    },
    [WorkflowStepActionKey.CreateVerificationEmailAddressFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationEmailAddressFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Email Address Verification',
    },
    [WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Gov ID Verification',
    },
    [WorkflowStepActionKey.CreateVerificationGovernmentIdNfcFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationGovernmentIdNfcFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Gov ID NFC Verification',
    },
    [WorkflowStepActionKey.CreateVerificationPhoneNumberFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationPhoneNumberFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Phone Number Verification',
    },
    [WorkflowStepActionKey.CreateVerificationSelfieFromInquiry]: {
      key: WorkflowStepActionKey.CreateVerificationSelfieFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Selfie Verification',
    },
    [WorkflowStepActionKey.RunVerificationDatabaseBusiness]: {
      key: WorkflowStepActionKey.RunVerificationDatabaseBusiness,
      group: ActionGroup.Verification,
      label: 'Run Business Registry Verification',
    },
    [WorkflowStepActionKey.RunVerificationDatabaseBusinessFootprintFromInquiry]: {
      key: WorkflowStepActionKey.RunVerificationDatabaseBusinessFootprintFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Business Footprint Verification',
    },
    [WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry]: {
      key: WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Business Website Verification',
    },
    [WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry]: {
      key: WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Database Phone Carrier Verification',
    },
    [WorkflowStepActionKey.RunVerificationDatabaseSerproFromInquiry]: {
      key: WorkflowStepActionKey.RunVerificationDatabaseSerproFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Database Serpro Verification',
    },
    [WorkflowStepActionKey.RunVerificationDatabaseTinFromInquiry]: {
      key: WorkflowStepActionKey.RunVerificationDatabaseTinFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Database Tin Verification',
    },
    [WorkflowStepActionKey.RunVerificationDatabaseVatNumberFromInquiry]: {
      key: WorkflowStepActionKey.RunVerificationDatabaseVatNumberFromInquiry,
      group: ActionGroup.Verification,
      label: 'Run Database VAT Number Verification',
    },
    [WorkflowStepActionKey.UpdateAccount]: {
      key: WorkflowStepActionKey.UpdateAccount,
      group: ActionGroup.InquiryFlow,
      label: 'Update Account',
    },
    [WorkflowStepActionKey.UpdateAccountFieldsFromInquiry]: {
      key: WorkflowStepActionKey.UpdateAccountFieldsFromInquiry,
      group: ActionGroup.InquiryFlow,
      label: 'Update Account from Inquiry',
    },
    [WorkflowStepActionKey.StandardizeIdDetails]: {
      key: WorkflowStepActionKey.StandardizeIdDetails,
      group: ActionGroup.Verification,
      label: 'Standardize ID Details',
    },
    [WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDocument]: {
      key: WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDocument,
      group: ActionGroup.InquiryFlow,
      label: 'Update Inquiry Fields From Verification Document',
    },
    [WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabaseStandard]: {
      key: WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabaseStandard,
      group: ActionGroup.InquiryFlow,
      label: 'Update Inquiry Fields From Verification Database Standard',
    },
    [WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabase]: {
      key: WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabase,
      group: ActionGroup.InquiryFlow,
      label: 'Update Inquiry Fields from Verification Database',
    },
    [WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationGovernmentId]: {
      key: WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationGovernmentId,
      group: ActionGroup.InquiryFlow,
      label: 'Update Inquiry Fields from Verification Government ID',
    },
    [WorkflowStepActionKey.UpdateInquiryFromPersona]: {
      key: WorkflowStepActionKey.UpdateInquiryFromPersona,
      group: ActionGroup.InquiryFlow,
      label: 'Update Inquiry Fields from Persona',
    },
    [WorkflowStepActionKey.UpdateInquiryNextStepName]: {
      key: WorkflowStepActionKey.UpdateInquiryNextStepName,
      group: ActionGroup.InquiryFlow,
      label: 'Update Next Step',
    },
    [WorkflowStepActionKey.UpdateInquiryStateDatabaseError]: {
      key: WorkflowStepActionKey.UpdateInquiryStateDatabaseError,
      group: ActionGroup.InquiryFlow,
      label: 'Update Inquiry State with Database Error',
    },
    [WorkflowStepActionKey.UpdateInquiryStateDocumentError]: {
      key: WorkflowStepActionKey.UpdateInquiryStateDocumentError,
      group: ActionGroup.InquiryFlow,
      label: 'Update Inquiry State with Document Error',
    },
    [WorkflowStepActionKey.UpdateInquiryStateGovernmentIdError]: {
      key: WorkflowStepActionKey.UpdateInquiryStateGovernmentIdError,
      group: ActionGroup.InquiryFlow,
      label: 'Update Inquiry State with Government ID Error',
    },
    [WorkflowStepActionKey.UpdateInquiryStateSelfieError]: {
      key: WorkflowStepActionKey.UpdateInquiryStateSelfieError,
      group: ActionGroup.InquiryFlow,
      label: 'Update Inquiry State with Selfie Error',
    },
    [WorkflowStepActionKey.ConfirmLatestVerificationEmailAddress]: {
      key: WorkflowStepActionKey.ConfirmLatestVerificationEmailAddress,
      group: ActionGroup.InquiryFlow,
      label: 'Confirm Email Address',
    },
    [WorkflowStepActionKey.ResendConfirmationCodeLatestVerificationEmailAddress]: {
      key: WorkflowStepActionKey.ResendConfirmationCodeLatestVerificationEmailAddress,
      group: ActionGroup.InquiryFlow,
      label: 'Resend Email Address Confirmation Code',
    },
    [WorkflowStepActionKey.ConfirmLatestVerificationPhoneNumber]: {
      key: WorkflowStepActionKey.ConfirmLatestVerificationPhoneNumber,
      group: ActionGroup.InquiryFlow,
      label: 'Confirm Phone Number',
    },
    [WorkflowStepActionKey.ResendConfirmationCodeLatestVerificationPhoneNumber]: {
      key: WorkflowStepActionKey.ResendConfirmationCodeLatestVerificationPhoneNumber,
      group: ActionGroup.InquiryFlow,
      label: 'Resend SMS Confirmation Code',
    },
    [WorkflowStepActionKey.ProcessLatestVerificationCertificateKorea]: {
      key: WorkflowStepActionKey.ProcessLatestVerificationCertificateKorea,
      group: ActionGroup.InquiryFlow,
      label: 'Process Korean Digital Certificate Verification Results',
    },
    [WorkflowStepActionKey.ProcessLatestVerificationDatabaseAadhaar]: {
      key: WorkflowStepActionKey.ProcessLatestVerificationDatabaseAadhaar,
      group: ActionGroup.InquiryFlow,
      label: 'Process Database Aadhaar Verification Results',
    },
    [WorkflowStepActionKey.ProcessLatestVerificationDigitalIdFinnishTrustNetwork]: {
      key: WorkflowStepActionKey.ProcessLatestVerificationDigitalIdFinnishTrustNetwork,
      group: ActionGroup.InquiryFlow,
      label: 'Process Finnish Trust Network Verification Results',
    },
    [WorkflowStepActionKey.ProcessLatestVerificationDigitalIdOneId]: {
      key: WorkflowStepActionKey.ProcessLatestVerificationDigitalIdOneId,
      group: ActionGroup.InquiryFlow,
      label: 'Process OneID Verification Results',
    },
    [WorkflowStepActionKey.ProcessLatestVerificationDigitalIdSingpass]: {
      key: WorkflowStepActionKey.ProcessLatestVerificationDigitalIdSingpass,
      group: ActionGroup.InquiryFlow,
      label: 'Process Singpass Verification Results',
    },
    [WorkflowStepActionKey.UpdateObject]: {
      key: WorkflowStepActionKey.UpdateObject,
      group: ActionGroup.Other,
      label: 'Update Object',
    },
    // Deprecated step action keys
    [DeprecatedWorkflowStepActionKey.CreateReportAdverseMedia]: {
      key: DeprecatedWorkflowStepActionKey.CreateReportAdverseMedia,
      group: ActionGroup.Report,
      label: 'Create Adverse Media Report',
    },
    [DeprecatedWorkflowStepActionKey.CreateReportEmailAddress]: {
      key: DeprecatedWorkflowStepActionKey.CreateReportEmailAddress,
      group: ActionGroup.Report,
      label: 'Create Email Risk Report',
    },
    [DeprecatedWorkflowStepActionKey.CreateReportPhoneNumber]: {
      key: DeprecatedWorkflowStepActionKey.CreateReportPhoneNumber,
      group: ActionGroup.Report,
      label: 'Create Phone Risk Report',
    },
    [DeprecatedWorkflowStepActionKey.CreateReportProfile]: {
      key: DeprecatedWorkflowStepActionKey.CreateReportProfile,
      group: ActionGroup.Report,
      label: 'Create Profile Report',
    },
    [DeprecatedWorkflowStepActionKey.CreateReportSocialMedia]: {
      key: DeprecatedWorkflowStepActionKey.CreateReportSocialMedia,
      group: ActionGroup.Report,
      label: 'Create Social Media Report',
    },
    [DeprecatedWorkflowStepActionKey.CreateReportSynthetic]: {
      key: DeprecatedWorkflowStepActionKey.CreateReportSynthetic,
      group: ActionGroup.Report,
      label: 'Create Synthetic Report',
    },
    [DeprecatedWorkflowStepActionKey.CreateReportWatchlist]: {
      key: DeprecatedWorkflowStepActionKey.CreateReportWatchlist,
      group: ActionGroup.Report,
      label: 'Create Watchlist Report',
    },
    [DeprecatedWorkflowStepActionKey.ResolveCase]: {
      key: DeprecatedWorkflowStepActionKey.ResolveCase,
      group: ActionGroup.Case,
      label: 'Resolve Case',
    },
    [DeprecatedWorkflowStepActionKey.UpdateInquiryFieldsFromVerification]: {
      key: DeprecatedWorkflowStepActionKey.UpdateInquiryFieldsFromVerification,
      group: ActionGroup.InquiryFlow,
      label: 'Update Inquiry Fields from Verification',
    },
    [DeprecatedWorkflowStepActionKey.GetAnalyticsMetric]: {
      key: DeprecatedWorkflowStepActionKey.GetAnalyticsMetric,
      group: ActionGroup.Analytics,
      label: 'Get Analytics Metric',
    },
  };
};

/**
 * These actions are shared across all types of workflows (except direct workflows).
 * For any new actions, unless you know for certain that it should be limited to specific workflow types,
 * start by adding them here.
 * It's easier to restrict the action later, vs. allowing it one by one across multiple deploys.
 */
const COMMON_ACTIONS = [
  WorkflowStepActionKey.AddItemToList,
  WorkflowStepActionKey.AddItemToListFace,
  WorkflowStepActionKey.AddCaseToCaseQueue,
  WorkflowStepActionKey.AddCommentToCase,
  WorkflowStepActionKey.ApproveInquiry,
  WorkflowStepActionKey.AssignCase,
  WorkflowStepActionKey.AttemptConfirmationCode,
  WorkflowStepActionKey.Calculate,
  WorkflowStepActionKey.ClearCaseSla,
  WorkflowStepActionKey.CopyTags,
  WorkflowStepActionKey.CreateDocumentGovernmentId,
  WorkflowStepActionKey.DeclineInquiry,
  WorkflowStepActionKey.EmailInquiryLink,
  WorkflowStepActionKey.EvaluateCode,
  WorkflowStepActionKey.FetchPersonaObject,
  WorkflowStepActionKey.FindOrCreateAccount,
  WorkflowStepActionKey.FindOrCreateTransaction,
  WorkflowStepActionKey.GPTGenerateMessage,
  WorkflowStepActionKey.HttpRequest,
  WorkflowStepActionKey.HubspotOAuthCreateObject,
  WorkflowStepActionKey.HubspotOAuthSearchObject,
  WorkflowStepActionKey.HubspotOAuthUpdateObject,
  WorkflowStepActionKey.HubspotUpdateObject,
  WorkflowStepActionKey.MarkInquiryForReview,
  WorkflowStepActionKey.NovaCreditCashAtlas,
  WorkflowStepActionKey.NovaCreditCreditPassport,
  WorkflowStepActionKey.RedactObject,
  WorkflowStepActionKey.RemoveItemFromList,
  WorkflowStepActionKey.RemoveTag,
  WorkflowStepActionKey.RunReportGeneric,
  WorkflowStepActionKey.RunVerificationGeneric,
  WorkflowStepActionKey.SalesforceCreateObject,
  WorkflowStepActionKey.SalesforceOAuthCreateRecord,
  WorkflowStepActionKey.SalesforceOAuthSearchRecord,
  WorkflowStepActionKey.SalesforceOAuthUpdateRecord,
  WorkflowStepActionKey.SalesforceUpdateObject,
  WorkflowStepActionKey.SendCaseMessage,
  WorkflowStepActionKey.SendConfirmationCode,
  WorkflowStepActionKey.SendEmail,
  WorkflowStepActionKey.SendSlackMessage,
  WorkflowStepActionKey.SlackOAuthSendMessage,
  WorkflowStepActionKey.SendSms,
  WorkflowStepActionKey.SetCaseSla,
  WorkflowStepActionKey.SetRelation,
  WorkflowStepActionKey.SmsInquiryLink,
  WorkflowStepActionKey.SubmitMiddeskBusinessLookup,
  WorkflowStepActionKey.TagObject,
  WorkflowStepActionKey.UnsetRelation,
  WorkflowStepActionKey.UpdateAccountStatus,
  WorkflowStepActionKey.UpdateCaseStatus,
  WorkflowStepActionKey.UpdateObject,
  WorkflowStepActionKey.ZendeskAddTagsToTicket,
  WorkflowStepActionKey.ZendeskOAuthCreateTicket,
  WorkflowStepActionKey.ZendeskOAuthUpdateTicket,
  WorkflowStepActionKey.ZendeskCreateTicket,
  WorkflowStepActionKey.ZendeskRemoveTagsFromTicket,
  WorkflowStepActionKey.ZendeskUpdateTicket,
];

/**
 * Used to determine in what contexts a workflow action is available in the workflow editor.
 */
const POSSIBLE_ACTIONS_FOR_TRIGGERS: Partial<
  Record<EventType | EventName | ApiModel, WorkflowStepActionKey[]>
> = {
  [ApiModel.TriggerApi]: [
    ...COMMON_ACTIONS,
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.AttachObjectsToCase,
    WorkflowStepActionKey.AttachObjectsToSAR,
    WorkflowStepActionKey.ChainalysisAddressScreening,
    WorkflowStepActionKey.ClearbitBusinessLookup,
    WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
    WorkflowStepActionKey.CreateCase,
    WorkflowStepActionKey.CreateInquiry,
    WorkflowStepActionKey.CreateSAR,
    WorkflowStepActionKey.CreateTransaction,
    WorkflowStepActionKey.EquifaxOneview,
    WorkflowStepActionKey.ListMatch,
    WorkflowStepActionKey.MxAccount,
    WorkflowStepActionKey.MxCreateUser,
    WorkflowStepActionKey.MxGetMicrodepositStatus,
    WorkflowStepActionKey.MxRequestConnectWidget,
    WorkflowStepActionKey.Query,
    WorkflowStepActionKey.RunGraphQuery,
    WorkflowStepActionKey.RunKyckrBusinessLookup,
    WorkflowStepActionKey.RunReportCryptoAddressWatchlist,
    WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
    WorkflowStepActionKey.RunReportSentilinkScores,
    WorkflowStepActionKey.ScheduleWorkflowRun,
    WorkflowStepActionKey.SyncGraph,
    WorkflowStepActionKey.TrmWalletScreening,
    WorkflowStepActionKey.UpdateTransactionStatus,
  ].sort(),
  [ApiModel.TriggerAccount]: [
    ...COMMON_ACTIONS,
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.AttachObjectsToCase,
    WorkflowStepActionKey.AttachObjectsToSAR,
    WorkflowStepActionKey.ChainalysisAddressScreening,
    WorkflowStepActionKey.ClearbitBusinessLookup,
    WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
    WorkflowStepActionKey.CreateCase,
    WorkflowStepActionKey.CreateInquiry,
    WorkflowStepActionKey.CreateSAR,
    WorkflowStepActionKey.CreateTransaction,
    WorkflowStepActionKey.EquifaxOneview,
    WorkflowStepActionKey.ListMatch,
    WorkflowStepActionKey.Query,
    WorkflowStepActionKey.RunGraphQuery,
    WorkflowStepActionKey.RunKyckrBusinessLookup,
    WorkflowStepActionKey.RunReportAddressLookup,
    WorkflowStepActionKey.RunReportAdverseMedia,
    WorkflowStepActionKey.RunReportBusinessLookup,
    WorkflowStepActionKey.RunReportBusinessAdverseMedia,
    WorkflowStepActionKey.RunReportBusinessAssociatedPersons,
    WorkflowStepActionKey.RunReportBusinessIndustryClassification,
    WorkflowStepActionKey.RunReportBusinessSocialMedia,
    WorkflowStepActionKey.RunReportBusinessWatchlist,
    WorkflowStepActionKey.RunReportCryptoAddressWatchlist,
    WorkflowStepActionKey.RunReportCustomList,
    WorkflowStepActionKey.RunReportEmailAddress,
    WorkflowStepActionKey.RunReportPhoneNumber,
    WorkflowStepActionKey.RunReportPoliticallyExposedPerson,
    WorkflowStepActionKey.RunReportProfile,
    WorkflowStepActionKey.RunReportProfileNonAuthoritative,
    WorkflowStepActionKey.RunReportScreening,
    WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
    WorkflowStepActionKey.RunReportSentilinkScores,
    WorkflowStepActionKey.RunReportSocialMedia,
    WorkflowStepActionKey.RunReportSynthetic,
    WorkflowStepActionKey.RunReportWatchlist,
    WorkflowStepActionKey.RunReportWithParams,
    WorkflowStepActionKey.ScheduleWorkflowRun,
    WorkflowStepActionKey.SyncGraph,
    WorkflowStepActionKey.TrmWalletScreening,
    WorkflowStepActionKey.TrustDeviceFromInquiry,
    WorkflowStepActionKey.UpdateTransactionStatus,
  ].sort(),
  [ApiModel.TriggerCase]: [
    ...COMMON_ACTIONS,
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.AttachObjectsToCase,
    WorkflowStepActionKey.AttachObjectsToSAR,
    WorkflowStepActionKey.ChainalysisAddressScreening,
    WorkflowStepActionKey.ClearbitBusinessLookup,
    WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
    WorkflowStepActionKey.CreateCase,
    WorkflowStepActionKey.CreateInquiry,
    WorkflowStepActionKey.CreateSAR,
    WorkflowStepActionKey.CreateTransaction,
    WorkflowStepActionKey.EquifaxOneview,
    WorkflowStepActionKey.ListMatch,
    WorkflowStepActionKey.Query,
    WorkflowStepActionKey.RunGraphQuery,
    WorkflowStepActionKey.RunKyckrBusinessLookup,
    WorkflowStepActionKey.RunReportAddressLookup,
    WorkflowStepActionKey.RunReportAdverseMedia,
    WorkflowStepActionKey.RunReportBusinessLookup,
    WorkflowStepActionKey.RunReportBusinessAdverseMedia,
    WorkflowStepActionKey.RunReportBusinessAssociatedPersons,
    WorkflowStepActionKey.RunReportBusinessSocialMedia,
    WorkflowStepActionKey.RunReportBusinessWatchlist,
    WorkflowStepActionKey.RunReportCryptoAddressWatchlist,
    WorkflowStepActionKey.RunReportCustomList,
    WorkflowStepActionKey.RunReportEmailAddress,
    WorkflowStepActionKey.RunReportPhoneNumber,
    WorkflowStepActionKey.RunReportPoliticallyExposedPerson,
    WorkflowStepActionKey.RunReportProfile,
    WorkflowStepActionKey.RunReportProfileNonAuthoritative,
    WorkflowStepActionKey.RunReportScreening,
    WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
    WorkflowStepActionKey.RunReportSentilinkScores,
    WorkflowStepActionKey.RunReportSocialMedia,
    WorkflowStepActionKey.RunReportSynthetic,
    WorkflowStepActionKey.RunReportWatchlist,
    WorkflowStepActionKey.RunReportWithParams,
    WorkflowStepActionKey.ScheduleWorkflowRun,
    WorkflowStepActionKey.SyncGraph,
    WorkflowStepActionKey.TrmWalletScreening,
    WorkflowStepActionKey.TrustDeviceFromInquiry,
    WorkflowStepActionKey.UpdateAccountFromUserInput,
    WorkflowStepActionKey.UpdateTransactionStatus,
  ].sort(),
  [ApiModel.TriggerDirect]: [
    // App Integration
    WorkflowStepActionKey.GPTGenerateMessage,
    WorkflowStepActionKey.MxAccount,
    WorkflowStepActionKey.MxCreateUser,
    WorkflowStepActionKey.MxGetMicrodepositStatus,
    WorkflowStepActionKey.MxRequestConnectWidget,
    WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
    WorkflowStepActionKey.SendSlackMessage,
    WorkflowStepActionKey.SlackOAuthSendMessage,
    WorkflowStepActionKey.SubmitMiddeskBusinessLookup,
    // Case
    WorkflowStepActionKey.CreateCase,
    WorkflowStepActionKey.AttachObjectsToCase,
    WorkflowStepActionKey.SendCaseMessage,
    // Communicate
    WorkflowStepActionKey.SendEmail,
    WorkflowStepActionKey.SendSms,
    // Compute
    WorkflowStepActionKey.Calculate,
    WorkflowStepActionKey.EvaluateCode,
    WorkflowStepActionKey.FetchPersonaObject,
    WorkflowStepActionKey.HttpRequest,
    // Graph
    WorkflowStepActionKey.RunGraphQuery,
    WorkflowStepActionKey.SyncGraph,
    // Inquiry
    WorkflowStepActionKey.CreateInquiry,
    WorkflowStepActionKey.ApproveInquiry,
    WorkflowStepActionKey.DeclineInquiry,
    WorkflowStepActionKey.MarkInquiryForReview,
    // Inquiry Flow
    WorkflowStepActionKey.ConfirmLatestVerificationEmailAddress,
    WorkflowStepActionKey.ConfirmLatestVerificationPhoneNumber,
    WorkflowStepActionKey.ProcessLatestVerificationCertificateKorea,
    WorkflowStepActionKey.ProcessLatestVerificationDatabaseAadhaar,
    WorkflowStepActionKey.ProcessLatestVerificationDigitalIdFinnishTrustNetwork,
    WorkflowStepActionKey.ProcessLatestVerificationDigitalIdOneId,
    WorkflowStepActionKey.ProcessLatestVerificationDigitalIdSingpass,
    WorkflowStepActionKey.ResendConfirmationCodeLatestVerificationEmailAddress,
    WorkflowStepActionKey.ResendConfirmationCodeLatestVerificationPhoneNumber,
    WorkflowStepActionKey.UpdateAccountFieldsFromInquiry,
    WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabase,
    WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDocument,
    WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationGovernmentId,
    // Lists
    WorkflowStepActionKey.ListMatch,
    // Other
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.CopyTags,
    WorkflowStepActionKey.RemoveTag,
    WorkflowStepActionKey.TagObject,
    WorkflowStepActionKey.UpdateObject,
    // Report
    WorkflowStepActionKey.RunReportGeneric,
    // Verifications
    WorkflowStepActionKey.CreateVerificationAamvaFromInquiry,
    WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry,
    WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry,
    WorkflowStepActionKey.CreateVerificationDatabaseAadhaarFromInquiry,
    WorkflowStepActionKey.CreateVerificationDatabaseStandardFromInquiry,
    WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry,
    WorkflowStepActionKey.CreateVerificationDigitalIdFinnishTrustNetworkFromInquiry,
    WorkflowStepActionKey.CreateVerificationDigitalIdOneIdFromInquiry,
    WorkflowStepActionKey.CreateVerificationDigitalIdSingpassFromInquiry,
    WorkflowStepActionKey.CreateVerificationDigitalIdUkSharecodeFromInquiry,
    WorkflowStepActionKey.CreateVerificationDocumentFromInquiry,
    WorkflowStepActionKey.CreateVerificationEmailAddressFromInquiry,
    WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry,
    WorkflowStepActionKey.CreateVerificationGovernmentIdNfcFromInquiry,
    WorkflowStepActionKey.CreateVerificationPhoneNumberFromInquiry,
    WorkflowStepActionKey.CreateVerificationSelfieFromInquiry,
    WorkflowStepActionKey.RunVerificationDatabaseBusiness,
    WorkflowStepActionKey.RunVerificationDatabaseBusinessFootprintFromInquiry,
    WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry,
    WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry,
    WorkflowStepActionKey.RunVerificationDatabaseTinFromInquiry,
    WorkflowStepActionKey.RunVerificationDatabaseVatNumberFromInquiry,
    WorkflowStepActionKey.StandardizeIdDetails,
    // Account,
    WorkflowStepActionKey.FindOrCreateAccount,
    WorkflowStepActionKey.UpdateAccountStatus,
  ].sort(),
  [ApiModel.TriggerRecurring]: [
    ...COMMON_ACTIONS,
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.AttachObjectsToCase,
    WorkflowStepActionKey.AttachObjectsToSAR,
    WorkflowStepActionKey.ChainalysisAddressScreening,
    WorkflowStepActionKey.ClearbitBusinessLookup,
    WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
    WorkflowStepActionKey.CreateCase,
    WorkflowStepActionKey.CreateInquiry,
    WorkflowStepActionKey.CreateSAR,
    WorkflowStepActionKey.CreateTransaction,
    WorkflowStepActionKey.EquifaxOneview,
    WorkflowStepActionKey.LabelObject,
    WorkflowStepActionKey.ListMatch,
    WorkflowStepActionKey.Query,
    WorkflowStepActionKey.RunGraphQuery,
    WorkflowStepActionKey.RunKyckrBusinessLookup,
    WorkflowStepActionKey.RunReportAddressLookup,
    WorkflowStepActionKey.RunReportAdverseMedia,
    WorkflowStepActionKey.RunReportBusinessLookup,
    WorkflowStepActionKey.RunReportBusinessAdverseMedia,
    WorkflowStepActionKey.RunReportBusinessAssociatedPersons,
    WorkflowStepActionKey.RunReportBusinessSocialMedia,
    WorkflowStepActionKey.RunReportBusinessWatchlist,
    WorkflowStepActionKey.RunReportCryptoAddressWatchlist,
    WorkflowStepActionKey.RunReportCustomList,
    WorkflowStepActionKey.RunReportEmailAddress,
    WorkflowStepActionKey.RunReportPhoneNumber,
    WorkflowStepActionKey.RunReportPoliticallyExposedPerson,
    WorkflowStepActionKey.RunReportProfile,
    WorkflowStepActionKey.RunReportProfileNonAuthoritative,
    WorkflowStepActionKey.RunReportScreening,
    WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
    WorkflowStepActionKey.RunReportSentilinkScores,
    WorkflowStepActionKey.RunReportSocialMedia,
    WorkflowStepActionKey.RunReportSynthetic,
    WorkflowStepActionKey.RunReportWatchlist,
    WorkflowStepActionKey.ScheduleWorkflowRun,
    WorkflowStepActionKey.SyncGraph,
    WorkflowStepActionKey.TrmWalletScreening,
    WorkflowStepActionKey.TrustDeviceFromInquiry,
    WorkflowStepActionKey.UpdateTransactionStatus,
  ].sort(),
  [ApiModel.TriggerSchedule]: [
    ...COMMON_ACTIONS,
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.AttachObjectsToCase,
    WorkflowStepActionKey.AttachObjectsToSAR,
    WorkflowStepActionKey.ChainalysisAddressScreening,
    WorkflowStepActionKey.ClearbitBusinessLookup,
    WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
    WorkflowStepActionKey.CreateCase,
    WorkflowStepActionKey.CreateInquiry,
    WorkflowStepActionKey.CreateSAR,
    WorkflowStepActionKey.CreateTransaction,
    WorkflowStepActionKey.EquifaxOneview,
    WorkflowStepActionKey.LabelObject,
    WorkflowStepActionKey.ListMatch,
    WorkflowStepActionKey.Query,
    WorkflowStepActionKey.RunGraphQuery,
    WorkflowStepActionKey.RunKyckrBusinessLookup,
    WorkflowStepActionKey.RunReportAddressLookup,
    WorkflowStepActionKey.RunReportAdverseMedia,
    WorkflowStepActionKey.RunReportBusinessLookup,
    WorkflowStepActionKey.RunReportBusinessAdverseMedia,
    WorkflowStepActionKey.RunReportBusinessAssociatedPersons,
    WorkflowStepActionKey.RunReportBusinessSocialMedia,
    WorkflowStepActionKey.RunReportBusinessWatchlist,
    WorkflowStepActionKey.RunReportCryptoAddressWatchlist,
    WorkflowStepActionKey.RunReportCustomList,
    WorkflowStepActionKey.RunReportEmailAddress,
    WorkflowStepActionKey.RunReportPhoneNumber,
    WorkflowStepActionKey.RunReportPoliticallyExposedPerson,
    WorkflowStepActionKey.RunReportProfile,
    WorkflowStepActionKey.RunReportProfileNonAuthoritative,
    WorkflowStepActionKey.RunReportScreening,
    WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
    WorkflowStepActionKey.RunReportSentilinkScores,
    WorkflowStepActionKey.RunReportSocialMedia,
    WorkflowStepActionKey.RunReportSynthetic,
    WorkflowStepActionKey.RunReportWatchlist,
    WorkflowStepActionKey.ScheduleWorkflowRun,
    WorkflowStepActionKey.SyncGraph,
    WorkflowStepActionKey.TrmWalletScreening,
    WorkflowStepActionKey.TrustDeviceFromInquiry,
    WorkflowStepActionKey.UpdateTransactionStatus,
  ].sort(),
  [EventName.InquiryApproved]: [WorkflowStepActionKey.CreateInquiry].sort(),
  [EventName.InquiryCompleted]: [WorkflowStepActionKey.CreateInquiry].sort(),
  [EventName.InquiryDeclined]: [WorkflowStepActionKey.CreateInquiry].sort(),
  [EventName.InquiryExpired]: [WorkflowStepActionKey.CreateInquiry].sort(),
  [EventName.InquiryFailed]: [WorkflowStepActionKey.CreateInquiry].sort(),
  [EventName.InquiryMarkedForReview]: [WorkflowStepActionKey.CreateInquiry].sort(),
  [EventType.Account]: [
    ...COMMON_ACTIONS,
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.ChainalysisAddressScreening,
    WorkflowStepActionKey.ClearbitBusinessLookup,
    WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
    WorkflowStepActionKey.CreateCase,
    WorkflowStepActionKey.CreateInquiry,
    WorkflowStepActionKey.CreateSAR,
    WorkflowStepActionKey.CreateTransaction,
    WorkflowStepActionKey.EquifaxOneview,
    WorkflowStepActionKey.ListMatch,
    WorkflowStepActionKey.MxAccount,
    WorkflowStepActionKey.Query,
    WorkflowStepActionKey.RunGraphQuery,
    WorkflowStepActionKey.RunKyckrBusinessLookup,
    WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
    WorkflowStepActionKey.RunReportSentilinkScores,
    WorkflowStepActionKey.ScheduleWorkflowRun,
    WorkflowStepActionKey.SyncGraph,
    WorkflowStepActionKey.TrmWalletScreening,
    WorkflowStepActionKey.UpdateTransactionStatus,
  ].sort(),
  [EventType.Case]: [
    ...COMMON_ACTIONS,
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.AttachObjectsToCase,
    WorkflowStepActionKey.AttachObjectsToSAR,
    WorkflowStepActionKey.CreateInquiry,
    WorkflowStepActionKey.CreateSAR,
    WorkflowStepActionKey.ListMatch,
    WorkflowStepActionKey.Query,
    WorkflowStepActionKey.RunGraphQuery,
    WorkflowStepActionKey.ScheduleWorkflowRun,
    WorkflowStepActionKey.SyncGraph,
    WorkflowStepActionKey.UpdateTransactionStatus,
  ].sort(),
  [EventName.CaseResolved]: [WorkflowStepActionKey.CreateCase].sort(),
  [EventName.CaseReopened]: [WorkflowStepActionKey.CreateCase].sort(),
  [EventName.CaseStatusUpdated]: [WorkflowStepActionKey.CreateCase].sort(),
  [EventType.CaseMessage]: [
    ...COMMON_ACTIONS,
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.AttachObjectsToCase,
    WorkflowStepActionKey.AttachObjectsToSAR,
    WorkflowStepActionKey.CreateInquiry,
    WorkflowStepActionKey.CreateSAR,
    WorkflowStepActionKey.ListMatch,
    WorkflowStepActionKey.Query,
    WorkflowStepActionKey.RunGraphQuery,
    WorkflowStepActionKey.SyncGraph,
    WorkflowStepActionKey.UpdateTransactionStatus,
  ].sort(),
  [EventType.Inquiry]: [
    ...COMMON_ACTIONS,
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.AttachObjectsToCase,
    WorkflowStepActionKey.AttachObjectsToSAR,
    WorkflowStepActionKey.ChainalysisAddressScreening,
    WorkflowStepActionKey.ClearbitBusinessLookup,
    WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
    WorkflowStepActionKey.CreateCase,
    WorkflowStepActionKey.CreateSAR,
    WorkflowStepActionKey.CreateTransaction,
    WorkflowStepActionKey.EquifaxOneview,
    WorkflowStepActionKey.ListMatch,
    WorkflowStepActionKey.MxAccount,
    WorkflowStepActionKey.Query,
    WorkflowStepActionKey.RunGraphQuery,
    WorkflowStepActionKey.RunKyckrBusinessLookup,
    WorkflowStepActionKey.RunReportAddressLookup,
    WorkflowStepActionKey.RunReportAdverseMedia,
    WorkflowStepActionKey.RunReportBusinessLookup,
    WorkflowStepActionKey.RunReportBusinessAdverseMedia,
    WorkflowStepActionKey.RunReportBusinessAssociatedPersons,
    WorkflowStepActionKey.RunReportBusinessSocialMedia,
    WorkflowStepActionKey.RunReportBusinessWatchlist,
    WorkflowStepActionKey.RunReportCryptoAddressWatchlist,
    WorkflowStepActionKey.RunReportCustomList,
    WorkflowStepActionKey.RunReportEmailAddress,
    WorkflowStepActionKey.RunReportPhoneNumber,
    WorkflowStepActionKey.RunReportPoliticallyExposedPerson,
    WorkflowStepActionKey.RunReportProfile,
    WorkflowStepActionKey.RunReportProfileNonAuthoritative,
    WorkflowStepActionKey.RunReportScreening,
    WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
    WorkflowStepActionKey.RunReportSentilinkScores,
    WorkflowStepActionKey.RunReportSocialMedia,
    WorkflowStepActionKey.RunReportSynthetic,
    WorkflowStepActionKey.RunReportWatchlist,
    WorkflowStepActionKey.ScheduleWorkflowRun,
    WorkflowStepActionKey.SyncGraph,
    WorkflowStepActionKey.TrmWalletScreening,
    WorkflowStepActionKey.TrustDeviceFromInquiry,
    WorkflowStepActionKey.UpdateTransactionStatus,
  ].sort(),
  [EventType.InquiryTemplateVersion]: [WorkflowStepActionKey.SendSlackMessage].sort(),
  [EventType.Report]: [
    ...COMMON_ACTIONS,
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.AttachObjectsToCase,
    WorkflowStepActionKey.AttachObjectsToSAR,
    WorkflowStepActionKey.ChainalysisAddressScreening,
    WorkflowStepActionKey.ClearbitBusinessLookup,
    WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
    WorkflowStepActionKey.CreateCase,
    WorkflowStepActionKey.CreateInquiry,
    WorkflowStepActionKey.CreateSAR,
    WorkflowStepActionKey.CreateTransaction,
    WorkflowStepActionKey.EquifaxOneview,
    WorkflowStepActionKey.Query,
    WorkflowStepActionKey.RunKyckrBusinessLookup,
    WorkflowStepActionKey.RunReportBusinessIndustryClassification,
    WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
    WorkflowStepActionKey.RunReportSentilinkScores,
    WorkflowStepActionKey.ScheduleWorkflowRun,
    WorkflowStepActionKey.TrmWalletScreening,
  ].sort(),
  [EventType.SAR]: [
    ...COMMON_ACTIONS,
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.AttachObjectsToCase,
    WorkflowStepActionKey.AttachObjectsToSAR,
    WorkflowStepActionKey.CreateCase,
    WorkflowStepActionKey.CreateInquiry,
    WorkflowStepActionKey.CreateTransaction,
    WorkflowStepActionKey.ListMatch,
    WorkflowStepActionKey.RunGraphQuery,
    WorkflowStepActionKey.ScheduleWorkflowRun,
    WorkflowStepActionKey.SyncGraph,
    WorkflowStepActionKey.UpdateTransactionStatus,
  ].sort(),
  [EventType.Transaction]: [
    ...COMMON_ACTIONS,
    WorkflowStepActionKey.AttachObjects,
    WorkflowStepActionKey.AttachObjectsToCase,
    WorkflowStepActionKey.AttachObjectsToSAR,
    WorkflowStepActionKey.ChainalysisAddressScreening,
    WorkflowStepActionKey.ClearbitBusinessLookup,
    WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
    WorkflowStepActionKey.CreateCase,
    WorkflowStepActionKey.CreateInquiry,
    WorkflowStepActionKey.CreateSAR,
    WorkflowStepActionKey.CreateTransaction,
    WorkflowStepActionKey.EquifaxOneview,
    WorkflowStepActionKey.ListMatch,
    WorkflowStepActionKey.MxAccount,
    WorkflowStepActionKey.MxCreateUser,
    WorkflowStepActionKey.MxGetMicrodepositStatus,
    WorkflowStepActionKey.MxRequestConnectWidget,
    WorkflowStepActionKey.LabelObject,
    WorkflowStepActionKey.Query,
    WorkflowStepActionKey.RunGraphQuery,
    WorkflowStepActionKey.RunKyckrBusinessLookup,
    WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
    WorkflowStepActionKey.RunReportSentilinkScores,
    WorkflowStepActionKey.ScheduleWorkflowRun,
    WorkflowStepActionKey.SyncGraph,
    WorkflowStepActionKey.TrmWalletScreening,
    WorkflowStepActionKey.UpdateTransactionStatus,
  ].sort(),
};

const ACTION_TO_REQUIRED_VERIFICATION_TEMPLATE_ID: Partial<
  Record<WorkflowStepActionKey, ApiModel>
> = {
  [WorkflowStepActionKey.CreateVerificationAamvaFromInquiry]: ApiModel.VerificationTemplateAamva,
  [WorkflowStepActionKey.CreateVerificationCertificateKoreaFromInquiry]:
    ApiModel.VerificationTemplateCertificateKorea,
  [WorkflowStepActionKey.CreateVerificationDatabaseFromInquiry]:
    ApiModel.VerificationTemplateDatabase,
  [WorkflowStepActionKey.CreateVerificationDatabaseAadhaarFromInquiry]:
    ApiModel.VerificationTemplateDatabaseAadhaar,
  [WorkflowStepActionKey.ProcessLatestVerificationDatabaseAadhaar]:
    ApiModel.VerificationTemplateDatabaseAadhaar,
  [WorkflowStepActionKey.CreateVerificationDatabaseStandardFromInquiry]:
    ApiModel.VerificationTemplateDatabaseStandard,
  [WorkflowStepActionKey.CreateVerificationDatabaseEcbsvFromInquiry]:
    ApiModel.VerificationTemplateDatabaseEcbsv,
  [WorkflowStepActionKey.CreateVerificationDigitalIdFinnishTrustNetworkFromInquiry]:
    ApiModel.VerificationTemplateDigitalIdFinnishTrustNetwork,
  [WorkflowStepActionKey.ProcessLatestVerificationDigitalIdFinnishTrustNetwork]:
    ApiModel.VerificationTemplateDigitalIdFinnishTrustNetwork,
  [WorkflowStepActionKey.CreateVerificationDigitalIdOneIdFromInquiry]:
    ApiModel.VerificationTemplateDigitalIdOneId,
  [WorkflowStepActionKey.ProcessLatestVerificationDigitalIdOneId]:
    ApiModel.VerificationTemplateDigitalIdOneId,
  [WorkflowStepActionKey.CreateVerificationDigitalIdSingpassFromInquiry]:
    ApiModel.VerificationTemplateDigitalIdSingpass,
  [WorkflowStepActionKey.ProcessLatestVerificationDigitalIdSingpass]:
    ApiModel.VerificationTemplateDigitalIdSingpass,
  [WorkflowStepActionKey.CreateVerificationDigitalIdUkSharecodeFromInquiry]:
    ApiModel.VerificationTemplateDigitalIdUkSharecode,
  [WorkflowStepActionKey.CreateVerificationDocumentFromInquiry]:
    ApiModel.VerificationTemplateDocument,
  [WorkflowStepActionKey.CreateVerificationEmailAddressFromInquiry]:
    ApiModel.VerificationTemplateEmailAddress,
  [WorkflowStepActionKey.CreateVerificationGovernmentIdFromInquiry]:
    ApiModel.VerificationTemplateGovernmentId,
  [WorkflowStepActionKey.CreateVerificationGovernmentIdNfcFromInquiry]:
    ApiModel.VerificationTemplateGovernmentIdNfc,
  [WorkflowStepActionKey.RunVerificationBusinessWebsiteFromInquiry]:
    ApiModel.VerificationTemplateBusinessWebsite,
  [WorkflowStepActionKey.CreateVerificationPhoneNumberFromInquiry]:
    ApiModel.VerificationTemplatePhoneNumber,
  [WorkflowStepActionKey.CreateVerificationSelfieFromInquiry]: ApiModel.VerificationTemplateSelfie,
  [WorkflowStepActionKey.RunVerificationDatabasePhoneCarrierFromInquiry]:
    ApiModel.VerificationTemplateDatabasePhoneCarrier,
};

export const MODULE_ACTION_KEYS: WorkflowStepActionKey[] = [
  WorkflowStepActionKey.AddCaseToCaseQueue,
  WorkflowStepActionKey.AddCommentToCase,
  WorkflowStepActionKey.AddItemToList,
  WorkflowStepActionKey.AddItemToListFace,
  WorkflowStepActionKey.ApproveInquiry,
  WorkflowStepActionKey.AssignCase,
  WorkflowStepActionKey.AttachObjects,
  WorkflowStepActionKey.AttachObjectsToCase,
  WorkflowStepActionKey.AttachObjectsToSAR,
  WorkflowStepActionKey.Calculate,
  WorkflowStepActionKey.ChainalysisAddressScreening,
  WorkflowStepActionKey.ClearbitBusinessLookup,
  WorkflowStepActionKey.ClearCaseSla,
  WorkflowStepActionKey.CoinbaseCheckCryptoRisk,
  WorkflowStepActionKey.CopyTags,
  WorkflowStepActionKey.CreateCase,
  WorkflowStepActionKey.CreateDocumentGovernmentId,
  WorkflowStepActionKey.CreateInquiry,
  WorkflowStepActionKey.CreateSAR,
  WorkflowStepActionKey.CreateTransaction,
  WorkflowStepActionKey.DeclineInquiry,
  WorkflowStepActionKey.EmailInquiryLink,
  WorkflowStepActionKey.EquifaxOneview,
  WorkflowStepActionKey.EvaluateCode,
  WorkflowStepActionKey.FetchPersonaObject,
  WorkflowStepActionKey.FindOrCreateAccount,
  WorkflowStepActionKey.GPTGenerateMessage,
  WorkflowStepActionKey.HttpRequest,
  WorkflowStepActionKey.HubspotOAuthCreateObject,
  WorkflowStepActionKey.HubspotOAuthSearchObject,
  WorkflowStepActionKey.HubspotOAuthUpdateObject,
  WorkflowStepActionKey.HubspotUpdateObject,
  WorkflowStepActionKey.LabelObject,
  WorkflowStepActionKey.ListMatch,
  WorkflowStepActionKey.MarkInquiryForReview,
  WorkflowStepActionKey.MxAccount,
  WorkflowStepActionKey.MxCreateUser,
  WorkflowStepActionKey.MxGetMicrodepositStatus,
  WorkflowStepActionKey.MxRequestConnectWidget,
  WorkflowStepActionKey.NovaCreditCashAtlas,
  WorkflowStepActionKey.NovaCreditCreditPassport,
  WorkflowStepActionKey.Query,
  WorkflowStepActionKey.RedactObject,
  WorkflowStepActionKey.RemoveItemFromList,
  WorkflowStepActionKey.RemoveTag,
  WorkflowStepActionKey.RunGraphQuery,
  WorkflowStepActionKey.RunKyckrBusinessLookup,
  WorkflowStepActionKey.RunReportGeneric,
  WorkflowStepActionKey.RunReportSentilinkApplicationRisk,
  WorkflowStepActionKey.RunReportSentilinkScores,
  WorkflowStepActionKey.RunReportWithParams,
  WorkflowStepActionKey.RunVerificationGeneric,
  WorkflowStepActionKey.SendCaseMessage,
  WorkflowStepActionKey.ScheduleWorkflowRun,
  WorkflowStepActionKey.SalesforceCreateObject,
  WorkflowStepActionKey.SalesforceOAuthCreateRecord,
  WorkflowStepActionKey.SalesforceOAuthSearchRecord,
  WorkflowStepActionKey.SalesforceOAuthUpdateRecord,
  WorkflowStepActionKey.SalesforceUpdateObject,
  WorkflowStepActionKey.SendEmail,
  WorkflowStepActionKey.SendSlackMessage,
  WorkflowStepActionKey.SendSms,
  WorkflowStepActionKey.SetCaseSla,
  WorkflowStepActionKey.SetRelation,
  WorkflowStepActionKey.SlackOAuthSendMessage,
  WorkflowStepActionKey.SmsInquiryLink,
  WorkflowStepActionKey.SyncGraph,
  WorkflowStepActionKey.TagObject,
  WorkflowStepActionKey.TrmWalletScreening,
  WorkflowStepActionKey.TrustDeviceFromInquiry,
  WorkflowStepActionKey.UnsetRelation,
  WorkflowStepActionKey.UpdateAccountStatus,
  WorkflowStepActionKey.UpdateCaseStatus,
  WorkflowStepActionKey.UpdateObject,
  WorkflowStepActionKey.UpdateTransactionStatus,
  WorkflowStepActionKey.ZendeskAddTagsToTicket,
  WorkflowStepActionKey.ZendeskCreateTicket,
  WorkflowStepActionKey.ZendeskOAuthCreateTicket,
  WorkflowStepActionKey.ZendeskOAuthUpdateTicket,
  WorkflowStepActionKey.ZendeskRemoveTagsFromTicket,
  WorkflowStepActionKey.ZendeskUpdateTicket,
];

export const getActionKeysForInputStep = (
  inputStep: WorkflowStepInput,
  actionMeta: WorkflowActionMeta,
): WorkflowStepActionKey[] => {
  const { trigger } = inputStep;
  if (!trigger) {
    return MODULE_ACTION_KEYS;
  }

  switch (trigger.type) {
    case ApiModel.TriggerApi:
    case ApiModel.TriggerAccount:
    case ApiModel.TriggerCase:
    case ApiModel.TriggerRecurring:
    case ApiModel.TriggerSchedule:
      return POSSIBLE_ACTIONS_FOR_TRIGGERS[trigger.type] || [];
    case ApiModel.TriggerComputedField: {
      const computedTriggerEventType = eventTypeForComputedFieldTrigger(trigger);
      if (!computedTriggerEventType) {
        return [];
      }

      return POSSIBLE_ACTIONS_FOR_TRIGGERS[computedTriggerEventType] || [];
    }
    case ApiModel.TriggerEvent: {
      const triggerEventName = trigger.attributes['event-name'];
      const triggerEventType = EVENT_NAME_TO_EVENT_TYPE[triggerEventName];
      const actionsForEventName = POSSIBLE_ACTIONS_FOR_TRIGGERS[triggerEventName] || [];
      const actionsForEventType = triggerEventType
        ? POSSIBLE_ACTIONS_FOR_TRIGGERS[triggerEventType]
        : [];

      return uniq(actionsForEventName.concat(actionsForEventType || []));
    }
    case ApiModel.TriggerDirect: {
      // renders ITE only actions
      const verificationTemplateTypes = actionMeta['verification-templates'].map(
        (template) => template.type,
      );
      return (
        POSSIBLE_ACTIONS_FOR_TRIGGERS[trigger.type]?.filter((action) => {
          const verificationTemplateType = ACTION_TO_REQUIRED_VERIFICATION_TEMPLATE_ID[action];
          if (verificationTemplateType) {
            return verificationTemplateTypes.includes(verificationTemplateType);
          }
          return true;
        }) || []
      );
    }
    default:
      assertNever(trigger);
  }
};
